// Sprint 12.5 ACC.B6 — per-section feedback wrapper for artefact bodies.
//
// Splits a markdown blob on h2 (## …) boundaries, renders each slice
// via the existing MarkdownRenderer (untouched), and appends a subtle
// thumbs-up/thumbs-down row after each section with an optional
// comment textarea.
//
// Section keys are stable slugs derived from heading text. Duplicate
// headings (rare but real) get a `-2`, `-3`, … suffix to keep the
// natural-key idempotency the API relies on for re-submit dedupe.
//
// Headerless artefacts (no h2) get a single feedback row with
// sectionKey: '_overall'.
//
// UX contract (per launch prompt + bug #813):
//   - Subtle by default, hover state in #7C3AED (Graphite Violet)
//   - Fire-and-forget: POST is best-effort; success shows a 1.5s
//     "Thanks" inline confirmation, no error surfacing
//   - Hidden entirely when tenant.qualityFeedbackEnabled === false
//     (the parent ArtefactDetailContent decides whether to mount us)
//   - Bug #813: clicking a thumb does NOT close the textarea — the
//     user can keep typing AFTER the rating is submitted, and
//     debounced/blur saves UPDATE the existing row via the API's
//     60-min idempotency window. A brief "Updated" flicker confirms
//     each follow-up save.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { apiFetch } from '../lib/api';
import MarkdownRenderer from './MarkdownRenderer';

interface Props {
  artefactId: string;
  body: string;
}

interface Section {
  sectionKey: string;
  heading:    string;          // empty when no heading (the '_overall' fallback)
  body:       string;
}

/**
 * Lower-case, strip non-alphanumerics, hyphenate spaces. Stable across
 * re-renders so the natural-key idempotency in the API holds.
 */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Split a markdown body on h2 (^## ) boundaries. Preserves the
 * heading line in each section (so MarkdownRenderer renders the h2)
 * but uses the slug of the heading text as the sectionKey.
 *
 * Headings before the first ## (preamble) form an unkeyed section
 * with sectionKey '_overall'. Same fallback applies to artefacts
 * that have no h2 at all.
 */
export function splitSections(body: string): Section[] {
  if (!body || !body.trim()) return [];

  const lines  = body.split(/\r?\n/);
  const groups: Array<{ heading: string; lines: string[] }> = [];
  let preamble: string[] = [];

  // Track whether we're inside a fenced code block (``` or ~~~). A
  // line like `## comment` inside Python/etc. source must NOT be
  // treated as an h2 heading — that's a markdown-aware concern the
  // naive line-prefix match would miss. Adversarial review round 1.
  let inFence = false;
  let fenceMarker = '';
  for (const line of lines) {
    const fence = line.match(/^(```|~~~)/);
    if (fence) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fence[1];
      } else if (line.startsWith(fenceMarker)) {
        // Closing fence — only matches the same delimiter that opened.
        inFence = false;
        fenceMarker = '';
      }
    }

    const m = !inFence ? line.match(/^##\s+(.+?)\s*$/) : null;
    if (m) {
      groups.push({ heading: m[1], lines: [line] });
    } else if (groups.length === 0) {
      preamble.push(line);
    } else {
      groups[groups.length - 1].lines.push(line);
    }
  }

  // No h2 anywhere — single overall section.
  if (groups.length === 0) {
    return [{ sectionKey: '_overall', heading: '', body }];
  }

  // Preamble (text before the first h2) gets its own _overall slot
  // when non-empty — otherwise drop. Whitespace-only preambles are
  // common (artefacts often start with `## Section` on line 1).
  const sections: Section[] = [];
  if (preamble.some(l => l.trim() !== '')) {
    sections.push({ sectionKey: '_overall', heading: '', body: preamble.join('\n') });
  }

  // Slug each heading; suffix duplicates with a 1-based index. Don't
  // dedupe by content — the natural-key idempotency we want is per
  // physical position in the doc, not per logical heading.
  const slugCounts = new Map<string, number>();
  for (const g of groups) {
    const base = slugify(g.heading) || 'section';
    const seen = slugCounts.get(base) ?? 0;
    slugCounts.set(base, seen + 1);
    const sectionKey = seen === 0 ? base : `${base}-${seen + 1}`;
    sections.push({ sectionKey, heading: g.heading, body: g.lines.join('\n') });
  }
  return sections;
}

/**
 * Top-level wrapper. ArtefactDetailContent mounts this only when the
 * tenant has qualityFeedbackEnabled === true. We don't re-check the
 * flag inside — the parent owns the gating decision.
 */
export default function ArtefactSectionFeedback({ artefactId, body }: Props) {
  const sections = useMemo(() => splitSections(body), [body]);

  if (sections.length === 0) {
    return <MarkdownRenderer>{body}</MarkdownRenderer>;
  }

  return (
    <>
      {sections.map(s => (
        <div key={s.sectionKey} data-testid={`artefact-section-${s.sectionKey}`}>
          <MarkdownRenderer>{s.body}</MarkdownRenderer>
          <FeedbackRow artefactId={artefactId} sectionKey={s.sectionKey} />
        </div>
      ))}
    </>
  );
}

// ── Per-section feedback row ─────────────────────────────────────────

// Status flicker shown next to the textarea after a save completes.
// 'thanks'  → fired right after the initial thumb click
// 'updated' → fired after each post-click comment save (debounced or blur)
type StatusFlicker = '' | 'thanks' | 'updated';

const FLICKER_MS = 1500;
// Debounce window for post-submit auto-save. Short enough that a
// user who pauses for a moment sees their comment land, long enough
// that a fast typist doesn't fire a POST per keystroke.
const DEBOUNCE_MS = 500;
const COMMENT_MAX = 200;

function FeedbackRow({ artefactId, sectionKey }: { artefactId: string; sectionKey: string }) {
  const [comment,           setComment]           = useState('');
  // null until the user clicks a thumb. Once set, the rating is
  // locked in for this row's lifetime — re-clicking the same thumb
  // is a no-op, and we don't expose a "change rating" affordance
  // (an over-engineered edge case for v1).
  const [submittedRating,   setSubmittedRating]   = useState<1 | 5 | null>(null);
  // True when the textarea should be visible. Pre-submit: opens when
  // the user clicks "Add a comment". Post-submit: always true so
  // the user can elaborate after the click (the load-bearing fix
  // for #813).
  const [showCommentInput,  setShowCommentInput]  = useState(false);
  const [status,            setStatus]            = useState<StatusFlicker>('');

  // Two refs intentionally — they answer different questions and a
  // single ref conflates them in a way that breaks retry-on-failure:
  //
  //   lastPersistedRef    — what we LAST TRIED to write (optimistic).
  //                         Written immediately on every fire so the
  //                         no-op short-circuit in handleBlur / the
  //                         debounce effect sees the latest intent
  //                         synchronously, without waiting for a
  //                         microtask flush. Vitest's synchronous
  //                         fireEvent path doesn't flush microtasks
  //                         between calls, so a state-based or
  //                         post-await shadow lags by one tick and
  //                         the short-circuit mis-fires a redundant
  //                         POST.
  //
  //   lastServerConfirmedRef — what the SERVER has actually confirmed
  //                         (only written inside the success branch
  //                         of the IIFE, after the await resolves).
  //                         Used as the rollback target on POST
  //                         failure so we restore to true server
  //                         state, not to whatever optimistic value
  //                         a sibling in-flight call wrote into the
  //                         "previous" closure.
  //
  // The split is what makes the both-fail-same-content case correct:
  // on Call 2's rollback, we restore to '' (server truth) rather than
  // to 'foo' (Call 1's optimistic write) — so the next user
  // interaction sees `trimmed !== lastPersistedRef.current` and
  // retries. With a single ref, Call 2's rollback would restore to
  // Call 1's optimistic value and the retry path would silently die.
  const lastPersistedRef       = useRef('');
  const lastServerConfirmedRef = useRef('');

  // Monotonic call counter used by fireFeedback's rollback guard to
  // determine "is this the LATEST in-flight call". Content-equality
  // alone (an earlier design) breaks when two calls share the same
  // trimmed content: a stale failure could roll back ref even after
  // a newer call wrote the same value. The id stamp gives
  // unambiguous ownership and pairs with the dual-ref split above.
  //
  // Note: this does NOT serialize HTTP delivery. Two concurrent
  // POSTs can still arrive at the server out of order, leaving the
  // older content as the stored row while the UI shows the newer
  // text and the "Updated." flicker. Codex P2 on PR #814 flagged
  // this; blueprint-reviewer round-4 graded it P3 ("self-corrects
  // on next user interaction"). The contract per the launch prompt
  // is fire-and-forget — debounce is 500ms, RTT is typically much
  // shorter, so the race window is small in practice. Adding
  // client-side serialization breaks the synchronous-test pattern
  // (microtasks don't flush between fireEvent calls); a server-side
  // revision check is out of scope for this PR. Documented here so
  // a future round can pick it up cleanly.
  const callIdRef = useRef(0);

  const statusTimerRef = useRef<number | null>(null);

  function flashStatus(next: 'thanks' | 'updated') {
    setStatus(next);
    if (statusTimerRef.current !== null) window.clearTimeout(statusTimerRef.current);
    statusTimerRef.current = window.setTimeout(() => setStatus(''), FLICKER_MS);
  }

  // Clear the flicker timer if the component unmounts mid-flicker.
  useEffect(() => {
    return () => {
      if (statusTimerRef.current !== null) window.clearTimeout(statusTimerRef.current);
    };
  }, []);

  /**
   * Fire the POST. Caller decides what flicker to show. Wrapped in
   * an async IIFE rather than a chained .catch so unhandled-
   * rejection trackers don't see a transient unhandled state
   * between Promise creation and microtask catch.
   *
   * The API does an UPSERT keyed on (user, artefact, sectionKey)
   * within a 60-min window — same payload on a follow-up call
   * UPDATEs the existing row instead of inserting a duplicate.
   * Bug #813 turns this property into the user-facing fix: the
   * thumb click and subsequent comment-blur both POST to the same
   * endpoint; only the first inserts.
   *
   * Three guarantees the implementation maintains together:
   *
   *   1. `lastPersistedRef` is written OPTIMISTICALLY before the
   *      await so the no-op short-circuit in handleBlur / debounce
   *      sees the latest intent synchronously (no microtask race).
   *
   *   2. `lastServerConfirmedRef` is written ONLY inside the
   *      success branch (post-await). It is the source of truth
   *      that the rollback restores to — never the in-flight
   *      "previous" value of the optimistic ref, which could be a
   *      sibling call's not-yet-confirmed write.
   *
   *   3. `callIdRef` stamps each fire. Rollback only proceeds if we
   *      are still the latest fire — a stale failure from an
   *      earlier sibling does nothing.
   *
   * The user-visible flicker (Thanks/Updated) is fired by callers
   * synchronously, regardless of POST outcome — the user always
   * sees their click register, even on transient network failure.
   */
  function fireFeedback(rating: 1 | 5, c: string): void {
    const trimmed = c.trim().slice(0, COMMENT_MAX);
    const myId    = ++callIdRef.current;
    lastPersistedRef.current = trimmed;
    void (async () => {
      try {
        await apiFetch(`/artefacts/${artefactId}/feedback`, {
          method: 'POST',
          body: JSON.stringify({
            sectionKey,
            rating,
            ...(trimmed ? { comment: trimmed } : {}),
          }),
        });
        // Only advance the confirmed-by-server ref if we're still
        // the latest in-flight call. With multiple concurrent POSTs
        // (Codex P2 — see callIdRef declaration for the race
        // tradeoff), an older response arriving last shouldn't
        // clobber a newer call's confirmed write.
        if (callIdRef.current === myId) {
          lastServerConfirmedRef.current = trimmed;
        }
      } catch {
        // Restore to true server state, but only if we're the
        // latest in-flight call. A stale failure (a newer fire
        // bumped the counter past us) does nothing — we'd be
        // clobbering a still-correct optimistic state that the
        // newer call owns.
        if (callIdRef.current === myId) {
          lastPersistedRef.current = lastServerConfirmedRef.current;
        }
      }
    })();
  }

  function submit(rating: 1 | 5) {
    if (submittedRating !== null) return;
    fireFeedback(rating, comment);
    setSubmittedRating(rating);
    // Keep (or open) the textarea so the user can elaborate after
    // the click. This is the load-bearing change for #813 — the old
    // behaviour replaced the row with a Thanks message and reset
    // showCommentInput to false 1.5s later, dropping any text the
    // user typed during that window.
    setShowCommentInput(true);
    flashStatus('thanks');
  }

  // Debounced auto-save: when the user types in the textarea AFTER
  // the initial submission, fire a follow-up POST 500ms after the
  // last keystroke. The API's idempotency window does the rest —
  // the existing row gets UPDATEd in place. No-op when the trimmed
  // comment matches what we last persisted (read via the ref so we
  // see updates that resolved on the prior microtask without
  // waiting for a re-render).
  useEffect(() => {
    if (submittedRating === null) return;
    const trimmed = comment.trim().slice(0, COMMENT_MAX);
    if (trimmed === lastPersistedRef.current) return;
    const t = window.setTimeout(() => {
      fireFeedback(submittedRating, comment);
      flashStatus('updated');
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // fireFeedback / flashStatus are stable closures over component
    // state — depending on them would re-run the effect on every
    // render. lastPersistedRef is intentionally NOT in deps because
    // refs don't trigger re-renders; we read its .current eagerly
    // inside the effect body each time deps change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment, submittedRating]);

  // Immediate save on blur: defence-in-depth against the
  // navigate-away-mid-debounce edge case. If the trimmed comment
  // matches what's already persisted, we no-op so a Tab-out without
  // editing doesn't fire a redundant POST.
  function handleBlur() {
    if (submittedRating === null) return;
    const trimmed = comment.trim().slice(0, COMMENT_MAX);
    if (trimmed === lastPersistedRef.current) return;
    fireFeedback(submittedRating, comment);
    flashStatus('updated');
  }

  const iconBtnBase: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    padding: 4,
    transition: 'color 120ms',
    display: 'inline-flex',
    alignItems: 'center',
  };
  const iconBtnStyle = (isThis: boolean): React.CSSProperties => ({
    ...iconBtnBase,
    color: isThis && submittedRating !== null
      ? '#7C3AED'                  // submitted rating — highlighted
      : 'var(--text-secondary)',
    cursor: submittedRating === null ? 'pointer' : 'default',
    opacity: submittedRating !== null && !isThis ? 0.4 : 1,
  });

  // Hover-color is applied only when the buttons are still clickable
  // (pre-submit) — post-submit they're disabled and shouldn't
  // pretend they'll respond.
  const hoverIn  = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (submittedRating !== null) return;
    e.currentTarget.style.color = '#7C3AED';
  };
  const hoverOut = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (submittedRating !== null) return;
    e.currentTarget.style.color = 'var(--text-secondary)';
  };

  return (
    <div
      data-testid={`feedback-row-${sectionKey}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        marginTop: 4,
        marginBottom: 12,
        fontFamily: 'var(--font-body)',
        fontSize: 12,
      }}
    >
      <button
        type="button"
        aria-label="Mark this section as helpful"
        data-testid={`feedback-up-${sectionKey}`}
        onClick={() => submit(5)}
        disabled={submittedRating !== null}
        style={iconBtnStyle(submittedRating === 5)}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
      >
        <ThumbsUp size={14} />
      </button>
      <button
        type="button"
        aria-label="Mark this section as needs work"
        data-testid={`feedback-down-${sectionKey}`}
        onClick={() => submit(1)}
        disabled={submittedRating !== null}
        style={iconBtnStyle(submittedRating === 1)}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
      >
        <ThumbsDown size={14} />
      </button>
      <div style={{ flex: 1 }}>
        {(!showCommentInput && submittedRating === null) ? (
          <button
            type="button"
            data-testid={`feedback-comment-toggle-${sectionKey}`}
            onClick={() => setShowCommentInput(true)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              color: 'var(--text-secondary)',
              opacity: 0.7,
            }}
          >
            Add a comment (optional)
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <textarea
              data-testid={`feedback-comment-${sectionKey}`}
              // Always autofocus on mount — the textarea only mounts
              // when the user has affirmatively asked for it (either
              // clicked "Add a comment" pre-submit, or clicked a thumb
              // which auto-opens the textarea for elaboration). In
              // both flows the user's next action is to type, so
              // grabbing focus saves a click. Note: React's
              // autoFocus only fires on initial mount, not on
              // re-renders, so a textarea that was already open
              // pre-submit doesn't re-grab focus when the thumb
              // gets clicked — that would yank focus out of the
              // textarea the user is mid-typing in.
              autoFocus
              rows={2}
              value={comment}
              maxLength={COMMENT_MAX}
              onChange={e => setComment(e.target.value)}
              onBlur={handleBlur}
              placeholder={
                submittedRating !== null
                  ? 'Add a comment to your rating (optional)'
                  : "Anything you'd like to share?"
              }
              style={{
                background: 'var(--surface-alt)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: '6px 8px',
                fontFamily: 'inherit',
                fontSize: 12,
                color: 'var(--text)',
                outline: 'none',
                resize: 'vertical',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 10,
                color: 'var(--text-secondary)',
              }}
            >
              {/* Status flicker — Thanks (initial submit) or Updated
                  (subsequent comment save). Same testid the original
                  test asserted on so existing tests keep passing. */}
              {status === 'thanks' && (
                <span data-testid={`feedback-thanks-${sectionKey}`} style={{ opacity: 0.85 }}>
                  Thanks for your feedback.
                </span>
              )}
              {status === 'updated' && (
                <span data-testid={`feedback-updated-${sectionKey}`} style={{ opacity: 0.85 }}>
                  Updated.
                </span>
              )}
              {!status && <span />}
              <span>{comment.length}/{COMMENT_MAX}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
